export type ShiftColorToken =
  | 'green-soft'
  | 'green'
  | 'red-soft'
  | 'red'
  | 'blue-soft'
  | 'blue'
  | 'amber-soft'
  | 'amber'
  | 'cyan-soft'
  | 'cyan'
  | 'neutral-soft'
  | 'neutral';

export type ShiftColorFamily =
  | 'green'
  | 'red'
  | 'blue'
  | 'amber'
  | 'cyan'
  | 'neutral';

type WxbCssToken = `--wx-${string}`;

export interface ShiftColorTone {
  token: ShiftColorToken;
  label: string;
  bgToken: WxbCssToken;
  fgToken: WxbCssToken;
  chartToken: WxbCssToken;
}

export interface ShiftColorFamilyOption {
  family: ShiftColorFamily;
  label: string;
  softToken: ShiftColorToken;
  solidToken: ShiftColorToken;
}

export const SHIFT_COLOR_TONES: Record<ShiftColorToken, ShiftColorTone> = {
  'green-soft': {
    token: 'green-soft',
    label: '浅绿',
    bgToken: '--wx-green-100',
    fgToken: '--wx-green-700',
    chartToken: '--wx-green-300',
  },
  green: {
    token: 'green',
    label: '绿色',
    bgToken: '--wx-green-700',
    fgToken: '--wx-bg',
    chartToken: '--wx-green-500',
  },
  'red-soft': {
    token: 'red-soft',
    label: '浅红',
    bgToken: '--wx-red-100',
    fgToken: '--wx-red-700',
    chartToken: '--wx-red-100',
  },
  red: {
    token: 'red',
    label: '红色',
    bgToken: '--wx-red-700',
    fgToken: '--wx-bg',
    chartToken: '--wx-red-500',
  },
  'blue-soft': {
    token: 'blue-soft',
    label: '浅蓝',
    bgToken: '--wx-blue-100',
    fgToken: '--wx-blue-700',
    chartToken: '--wx-blue-300',
  },
  blue: {
    token: 'blue',
    label: '蓝色',
    bgToken: '--wx-blue-700',
    fgToken: '--wx-bg',
    chartToken: '--wx-blue-500',
  },
  'amber-soft': {
    token: 'amber-soft',
    label: '浅琥珀',
    bgToken: '--wx-amber-100',
    fgToken: '--wx-amber-700',
    chartToken: '--wx-amber-100',
  },
  amber: {
    token: 'amber',
    label: '琥珀',
    bgToken: '--wx-amber-500',
    fgToken: '--wx-ink',
    chartToken: '--wx-amber-500',
  },
  'cyan-soft': {
    token: 'cyan-soft',
    label: '浅青',
    bgToken: '--wx-cyan-100',
    fgToken: '--wx-cyan-700',
    chartToken: '--wx-cyan-100',
  },
  cyan: {
    token: 'cyan',
    label: '青色',
    bgToken: '--wx-cyan-700',
    fgToken: '--wx-bg',
    chartToken: '--wx-cyan-500',
  },
  'neutral-soft': {
    token: 'neutral-soft',
    label: '浅灰',
    bgToken: '--wx-surface-3',
    fgToken: '--wx-fg-4',
    chartToken: '--wx-surface-3',
  },
  neutral: {
    token: 'neutral',
    label: '灰色',
    bgToken: '--wx-fg-3',
    fgToken: '--wx-bg',
    chartToken: '--wx-fg-3',
  },
};

export const SHIFT_COLOR_FAMILIES: Record<ShiftColorFamily, ShiftColorFamilyOption> = {
  green: {
    family: 'green',
    label: '绿色',
    softToken: 'green-soft',
    solidToken: 'green',
  },
  red: {
    family: 'red',
    label: '红色',
    softToken: 'red-soft',
    solidToken: 'red',
  },
  blue: {
    family: 'blue',
    label: '蓝色',
    softToken: 'blue-soft',
    solidToken: 'blue',
  },
  amber: {
    family: 'amber',
    label: '琥珀',
    softToken: 'amber-soft',
    solidToken: 'amber',
  },
  cyan: {
    family: 'cyan',
    label: '青色',
    softToken: 'cyan-soft',
    solidToken: 'cyan',
  },
  neutral: {
    family: 'neutral',
    label: '灰色',
    softToken: 'neutral-soft',
    solidToken: 'neutral',
  },
};

const SHIFT_COLOR_TOKEN_FAMILY: Record<ShiftColorToken, ShiftColorFamily> = {
  'green-soft': 'green',
  green: 'green',
  'red-soft': 'red',
  red: 'red',
  'blue-soft': 'blue',
  blue: 'blue',
  'amber-soft': 'amber',
  amber: 'amber',
  'cyan-soft': 'cyan',
  cyan: 'cyan',
  'neutral-soft': 'neutral',
  neutral: 'neutral',
};

export const SHIFT_COLOR_OPTIONS: ShiftColorTone[] = [
  SHIFT_COLOR_TONES['green-soft'],
  SHIFT_COLOR_TONES.green,
  SHIFT_COLOR_TONES['red-soft'],
  SHIFT_COLOR_TONES.red,
  SHIFT_COLOR_TONES['blue-soft'],
  SHIFT_COLOR_TONES.blue,
  SHIFT_COLOR_TONES['amber-soft'],
  SHIFT_COLOR_TONES.amber,
  SHIFT_COLOR_TONES['cyan-soft'],
  SHIFT_COLOR_TONES.cyan,
  SHIFT_COLOR_TONES['neutral-soft'],
  SHIFT_COLOR_TONES.neutral,
];

export const SHIFT_COLOR_FAMILY_OPTIONS: ShiftColorFamilyOption[] = [
  SHIFT_COLOR_FAMILIES.green,
  SHIFT_COLOR_FAMILIES.red,
  SHIFT_COLOR_FAMILIES.blue,
  SHIFT_COLOR_FAMILIES.amber,
  SHIFT_COLOR_FAMILIES.cyan,
  SHIFT_COLOR_FAMILIES.neutral,
];

export type ShiftColorDefaults = {
  display_color_token: ShiftColorToken;
  no_operation_color_token: ShiftColorToken;
  operation_color_token: ShiftColorToken;
};

type ShiftColorTokenSetInput = {
  display_color_token?: unknown;
  no_operation_color_token?: unknown;
  operation_color_token?: unknown;
};

export const isShiftColorToken = (value: unknown): value is ShiftColorToken =>
    typeof value === 'string' && value in SHIFT_COLOR_TONES;

export const resolveShiftColorToken = (
  value: unknown,
  fallback: ShiftColorToken = 'neutral-soft',
): ShiftColorToken => (isShiftColorToken(value) ? value : fallback);

export const getShiftColorTone = (
  value: unknown,
  fallback: ShiftColorToken = 'neutral-soft',
): ShiftColorTone => SHIFT_COLOR_TONES[resolveShiftColorToken(value, fallback)];

export const getShiftToneClass = (
  value: unknown,
  fallback: ShiftColorToken,
  prefix: string,
): string => `${prefix}${resolveShiftColorToken(value, fallback)}`;

const readShiftColorFamily = (value: unknown): ShiftColorFamily | null => {
  if (typeof value !== 'string') return null;
  if (value in SHIFT_COLOR_FAMILIES) return value as ShiftColorFamily;
  if (isShiftColorToken(value)) return SHIFT_COLOR_TOKEN_FAMILY[value];
  return null;
};

export const resolveShiftColorFamily = (
  value: unknown,
  fallback: ShiftColorFamily = 'green',
): ShiftColorFamily => readShiftColorFamily(value) ?? fallback;

export const resolveShiftColorFamilyFromTokens = (
  tokens: ShiftColorTokenSetInput,
  fallback: ShiftColorFamily = 'green',
): ShiftColorFamily => {
  const orderedValues = [
    tokens.operation_color_token,
    tokens.no_operation_color_token,
    tokens.display_color_token,
  ];
  for (const value of orderedValues) {
    const family = readShiftColorFamily(value);
    if (family) return family;
  }
  return fallback;
};

export const deriveShiftColorTokens = (family: ShiftColorFamily): ShiftColorDefaults => {
  const option = SHIFT_COLOR_FAMILIES[family];
  return {
    display_color_token: option.softToken,
    no_operation_color_token: option.softToken,
    operation_color_token: option.solidToken,
  };
};

interface ShiftColorDefaultInput {
  shift_code?: string | null;
  shift_name?: string | null;
  is_night_shift?: boolean | null;
  nominal_hours?: number | null;
  kind?: 'day' | 'night' | 'long' | 'rest' | 'leave' | string | null;
}

export const getDefaultShiftColorTokens = (shift: ShiftColorDefaultInput): ShiftColorDefaults => {
  const code = String(shift.shift_code ?? '').toUpperCase();
  const name = String(shift.shift_name ?? '').toLowerCase();
  const hasHours = shift.nominal_hours !== undefined && shift.nominal_hours !== null;
  const hours = Number(shift.nominal_hours ?? 0);

  if (shift.kind === 'leave') {
    return deriveShiftColorTokens('amber');
  }
  if (
    shift.kind === 'rest' ||
    code.includes('REST') ||
    code.includes('OFF') ||
    name.includes('休') ||
    (hasHours && hours <= 0)
  ) {
    return deriveShiftColorTokens('neutral');
  }
  if (
    shift.kind === 'night' ||
    shift.is_night_shift ||
    code.includes('NIGHT') ||
    name.includes('夜') ||
    name.includes('night')
  ) {
    return deriveShiftColorTokens('red');
  }
  if (shift.kind === 'long' || code.includes('LONG') || name.includes('长白') || name.includes('long')) {
    return deriveShiftColorTokens('blue');
  }
  return deriveShiftColorTokens('green');
};
